<script setup>
import { ref, watch, computed, useTemplateRef, nextTick } from "vue";
import { cities } from "api";

defineOptions({
  name: "CityAutocomplete",
});

const props = defineProps({
  endpoint: { type: Function, default: cities.autocomplete },
});

const I18n = window.I18n;
const opened = ref(false);
const searchName = ref("");
const searchCities = ref([]);
const highlightedIndex = ref(-1);
const autocomplete = useTemplateRef("autocomplete");
const input = useTemplateRef("autocomplete-input");

watch(searchName, async (newSearchName, oldSearchName) => {
  if (newSearchName.trim() === "") {
    searchCities.value = [];
    return;
  }
  await search(newSearchName);
});

watch(searchCities, async () => {
  highlightedIndex.value = -1;
});

const highlightedCities = computed(() => {
  const cities = searchName.value
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));

  return searchCities.value.map((city) => {
    const regex = new RegExp(`(${cities.join("|")})`, "ig");
    const highlightedName = city.name.replace(regex, "<strong>$1</strong>");
    const highlightedRegionLabel = city.regionLabel.replace(
      regex,
      "<strong>$1</strong>",
    );
    return { ...city, highlightedName, highlightedRegionLabel };
  });
});

async function open(resetAll = false) {
  if (resetAll) reset();
  opened.value = true;
  await nextTick();
  input.value.focus();
}

function close() {
  opened.value = false;
}

function reset() {
  searchName.value = "";
  searchCities.value = [];
}

async function search(name) {
  searchCities.value = await props.endpoint({ query: { name } });
}

function selectCity(city) {
  close();
  const event = new CustomEvent("citySelected", {
    bubbles: true,
    detail: { id: city.id, label: city.label },
  });
  autocomplete.value.dispatchEvent(event);
}

function highlightPrevious() {
  if (highlightedIndex.value <= 0) return;

  highlightedIndex.value -= 1;
}

function highlightNext() {
  if (highlightedIndex.value >= searchCities.value.length - 1) return;

  highlightedIndex.value += 1;
}

function selectCityFromIndex() {
  if (highlightedIndex.value < 0) return;

  const city = searchCities.value[highlightedIndex.value];
  selectCity(city);
}

defineExpose({ open, close });
</script>

<template>
  <Transition name="fade">
    <me-autocomplete
      ref="autocomplete"
      v-if="opened"
      @keydown.escape.prevent="close"
    >
      <div class="wrapper">
        <div class="inner">
          <input-box
            @keydown.up.prevent="highlightPrevious"
            @keydown.down.prevent="highlightNext"
            @keydown.shift.tab.prevent="highlightPrevious"
            @keydown.tab.exact.prevent="highlightNext"
            @keydown.enter.prevent="selectCityFromIndex"
          >
            <input
              type="text"
              v-model="searchName"
              ref="autocomplete-input"
              :placeholder="I18n.t('js.city_autocomplete.search')"
            />

            <ul class="autocomplete-list" v-if="searchCities.length > 0">
              <template
                v-for="(city, index) in highlightedCities"
                :key="city.id"
              >
                <li
                  :class="{ selected: index == highlightedIndex }"
                  @mouseover="highlightedIndex = index"
                >
                  <button type="button" @click="selectCity(city)">
                    <font-icon
                      class="geolocation--fill icon-size-xl"
                    ></font-icon>
                    <span>
                      <span v-html="city.highlightedName"></span>
                      <small v-html="city.highlightedRegionLabel"></small>
                    </span>
                  </button>
                </li>
              </template>
            </ul>
            <no-data v-if="searchName.length > 0 && searchCities.length <= 0">{{
              I18n.t("js.common.no_result")
            }}</no-data>
            <no-data v-else-if="searchCities.length <= 0">
              <span class="no-data-message">
                <font-icon class="no-data-icon search"></font-icon>
                <span class="no-data-label">{{
                  I18n.t("js.city_autocomplete.search_desc")
                }}</span>
              </span>
            </no-data>
          </input-box>
        </div>
      </div>
      <button type="button" class="overlay" @click="close">
        <span class="wrapper">
          <span class="inner">
            <span class="close">
              <font-icon class="cross icon-size-lg"></font-icon>
            </span>
          </span>
        </span>
      </button>
    </me-autocomplete>
  </Transition>
</template>

<style>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 350ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.no-data-message {
  display: grid;
  gap: var(--space-2);
}
.no-data-icon {
  font-size: 44px;
  color: var(--color-neutral-light);
}
</style>
